import { useTranslation } from 'react-i18next';
import { Button, DataTable } from '@airclou/ui';
import { Plus, Users } from 'lucide-react';
import type { WorkspaceUser } from '@/api/workspaces';
import { useAuth } from '../../contexts/AuthContext'
import { canManageOrg } from '../../navigation'
import { RoleUnknownNotice } from '../state/RoleUnknownNotice'

type Props = {
  users: WorkspaceUser[];
  columns: any[];
  sorting: any;
  onSortingChange: (value: any) => void;
  onAddUser: () => void;
  getRowHref?: (user: WorkspaceUser) => string;
  /** The page's first load. The table keeps its frame and shows a loading row,
   *  and the empty state is held back until the rows have answered — "no members
   *  yet" must not flash on a workspace that has some. */
  isLoading?: boolean;
};

export function WorkspaceUsersTab({ users, columns, sorting, onSortingChange, onAddUser, getRowHref, isLoading = false }: Props) {
  const { t } = useTranslation('dashboard');
  // Workspace writes are `TENANT_ROLE_SETS.MANAGE` on the API as of 2026-09-13
  // (role plan §6.4); `canManageOrg` mirrors that set. Reading stays open to any
  // member — only the writes move.
  const { user } = useAuth();
  const canManage = canManageOrg(user?.orgRole ?? undefined);

  const addButton = canManage ? (
    <Button onClick={onAddUser}>
      <Plus className="w-4 h-4 mr-2" />
      {t('workspaceTabs.addMember')}
    </Button>
  ) : null;

  return (
    <div>
      <div className="pb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">{t('workspaceTabs.members')}</h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('workspaceTabs.directMembers')}</p>
          </div>
          {addButton}
        </div>
        <RoleUnknownNotice />
      </div>

      <div>
        {users.length === 0 && !isLoading ? (
          <div className="p-12 text-center">
            <Users className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">{t('workspaceTabs.noUsersYet')}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">{t('workspaceTabs.noUsersHelp')}</p>
            {addButton}
          </div>
        ) : (
          <DataTable data={users} columns={columns} sorting={sorting} onSortingChange={onSortingChange} getRowHref={getRowHref} isLoading={isLoading && users.length === 0} />
        )}
      </div>
    </div>
  );
}
